import FilterCondition from '@/components/query/spec/filter-condition';
import FILTER_TYPES from '@/components/query/filter-types';
import ActiveFilter from '@/components/query/active-filter';
import timezone from '@/common/timezone';
import moment from 'moment-timezone';
import _ from '@/common/helpers';
import FilterOptions from '@/components/query/filter-options';
import filterDelimiters from './filter-delimiters';

/** @module FilterAdapter */

/* eslint-disable class-methods-use-this */
/**
 * Adapter class that use Filters as defined on the API back-end to determine context of metrics.
 */
export default class FilterAdapter {
    static oldFormatExp = /^\d{4}-\d{2}-\d{2}$/;

    /**
     * Convert a string representation of the `filter` into a collection of active filters
     * rehydrated with the values in said string.
     * @param {Array} query
     * @param {Object} filterSet
     * @returns {ActiveFilter[]}
     */
    parseQuery({query, filterSet}) {
        if (query) {
            const mapFilters = (definition) => {
                const [path, payload] = definition;
                const params = {payload, filter: filterSet[path]};
                return new ActiveFilter({
                    filter: filterSet[path],
                    condition: FilterAdapter.restoreConditionFromPayload(params),
                });
            };
            return query.map(mapFilters);
        }
        return null;
    }

    /**
     * Generate the array representation to store in the user's preferences.
     * This array can be used to be converted into a `filter` string via
     * `FilterAdapter.convertFiltersToString`.
     * @param {ActiveFilter[]} activeFilters
     * @returns {Array}
     */
    getQueryFromActiveFilters(activeFilters) {
        const convertArray = condition => condition.values;
        const convertNumericRange = condition => [condition.min, condition.max];
        const convertDateRange = (condition, filter = null) => {
            const {min, max} = condition;
            // when time picking is allowed don't skew the time of day
            if (filter && filter.options && filter.options.options.timePicker) {
                return [
                    min ? min.format() : null,
                    max ? max.format() : null,
                ];
            }
            // otherwise use startOf/endOf day
            return [
                min ? min.startOf('day').format() : null,
                max ? max.endOf('day').format() : null,
            ];
        };
        const map = {
            [FILTER_TYPES.text]: convertArray,
            [FILTER_TYPES.enum]: convertArray,
            [FILTER_TYPES.select]: convertArray,
            [FILTER_TYPES.boolean]: condition => condition.value.toString(),
            [FILTER_TYPES.numeric]: convertNumericRange,
            [FILTER_TYPES.datetime]: convertDateRange,
        };
        // eslint-disable-next-line max-len
        const queryMap = ({filter, condition}) => [filter.path, map[filter.type](condition, filter), filter.type];
        return activeFilters.map(queryMap);
    }

    /**
     * Get the initial value of an active filter if a condition
     * is present.
     * @param {ActiveFilter} activeFilter
     * @returns {*}
     */
    getInitialFilterValue(activeFilter) {
        const convertArray = ({condition}) => {
            if (condition.values) {
                return condition.values;
            }
            return null;
        };
        const map = {
            [FILTER_TYPES.text]: convertArray,
            [FILTER_TYPES.enum]: convertArray,
            [FILTER_TYPES.select]: convertArray,
            [FILTER_TYPES.boolean]: ({condition}) => (Array.isArray(condition.value)
                ? condition.value[0]
                : (condition.value === 'true' || condition.value === true)),
            [FILTER_TYPES.numeric]: ({condition}) => ({
                min: condition.min !== undefined ? condition.min : null,
                max: condition.max !== undefined ? condition.max : null,
            }),
            [FILTER_TYPES.datetime]: ({condition}) => ({
                min: condition.min || null,
                max: condition.max || null,
            }),
        };
        return map[activeFilter.filter.type](activeFilter);
    }

    /**
     * Get the internal payload of an active filter. Return the index to modify
     * in the collection and the data (FilterCondition) generated from the active filter.
     * @param {Number} index
     * @param {ActiveFilter} filter
     * @param {Object|null} assign
     * @param {Object|null} data
     * @returns {{index: Number, data: FilterCondition}}
     */
    getFilterPayload({
        index, filter, assign = null, data = null,
    }) {
        // check the initial condition parameters
        const conditionParams = data || {...filter.condition};
        const payload = {
            index,
            data: new FilterCondition(conditionParams),
        };
        // overwrite payload data as needed
        if (assign) {
            payload.data = {
                ...payload.data,
                ...assign,
            };
        }
        return payload;
    }

    /**
     * Created a blank active filter from the provided filter definition. This is used
     * whenever the user adds a new filter from the dropdown selection.
     * @param {FilterDefinition} filter
     * @returns {ActiveFilter}
     */
    createActiveFilter(filter) {
        return new ActiveFilter({
            filter,
            condition: new FilterCondition(),
        });
    }

    /**
     * Restores a FilterCondition instance from the string payload used to
     * express the values of the `filter`.
     * @param {string} payload
     * @param {FilterDefinition} filter
     * @returns {FilterCondition}
     */
    static restoreConditionFromPayload({payload, filter}) {
        const convertRange = () => {
            const data = {min: null, max: null};
            const [min, max] = payload;
            if (min === null) {
                data.max = max;
            } else if (max === null) {
                data.min = min;
            } else {
                data.min = min;
                data.max = max;
            }
            return data;
        };
        const convertDateRange = () => {
            const data = {min: null, max: null};
            const {min = null, max = null} = convertRange();
            if (min) {
                data.min = moment.utc(min);
                // convert old format to include time
                if (FilterAdapter.oldFormatExp.test(min)) {
                    data.min = data.min.startOf('day');
                }
            }
            if (max) {
                data.max = moment.utc(max);
                // convert old format to include time
                if (FilterAdapter.oldFormatExp.test(max)) {
                    data.max = data.max.endOf('day');
                }
            }

            return data;
        };
        const convertArray = () => ({
            values: payload,
        });
        const map = {
            [FILTER_TYPES.text]: convertArray,
            [FILTER_TYPES.select]: convertArray,
            [FILTER_TYPES.enum]: convertArray,
            [FILTER_TYPES.boolean]: () => ({value: payload}),
            [FILTER_TYPES.numeric]: convertRange,
            [FILTER_TYPES.datetime]: convertDateRange,
        };
        return new FilterCondition(map[filter.type]());
    }

    static restorePayloadFromCondition({condition, filter}) {
        const convertRange = () => [condition.min, condition.max];
        const convertDateRange = () => {
            const [min, max] = convertRange();
            return [
                min ? timezone.getRaw(min) : null,
                max ? timezone.getRaw(max) : null,
            ];
        };
        const convertArray = () => condition.values;
        const convertBoolean = () => !!condition.value;

        const map = {
            [FILTER_TYPES.text]: convertArray,
            [FILTER_TYPES.select]: convertArray,
            [FILTER_TYPES.enum]: convertArray,
            [FILTER_TYPES.boolean]: convertBoolean,
            [FILTER_TYPES.numeric]: convertRange,
            [FILTER_TYPES.datetime]: convertDateRange,
        };
        return map[filter.type]();
    }

    /**
     * Parse convertFiltersToString response to filterSet and query
     *
     * @param filter - filter string
     * @param fields - fields object (object similar to FilterSet init params)
     * @returns {Array}
     */
    static convertStringToFilters({filter, fields}) {
        /**
         * Prepare fields list
         */
        const filters = _.isBlank(filter)
            ? []
            : filter.split(filterDelimiters.filter)
                .filter(item => !_.isBlank(item))
                .map((item) => {
                    const delimiterPosition = item.indexOf(filterDelimiters.definition);
                    const field = item.substring(0, delimiterPosition);
                    const condition = item.substring(delimiterPosition + 1);
                    const path = field.replace(/^\//, '');
                    return {
                        path,
                        condition,
                        validate: null,
                        ...fields[path] ? {
                            filter: {
                                ...fields[path],
                                path,
                                name: path,
                            },
                        } : {},
                    };
                });
        /**
         * Returns string condition payload parsed as payload 1..10 -> [1, 10]
         */
        const parseConditionPayload = ({condition, type}) => {
            if (type === FILTER_TYPES.datetime) {
                return condition.split(filterDelimiters.range).map(_.restorePrimitive);
            }
            if (type === FILTER_TYPES.numeric) {
                return condition.split(filterDelimiters.range).map(_.restorePrimitive).map(v => +v);
            }
            if (type === FILTER_TYPES.boolean) {
                return condition === 'true';
            }
            return condition.includes(filterDelimiters.values)
                ? condition.split(filterDelimiters.values).map(_.restorePrimitive)
                : [_.restorePrimitive(condition)];
        };

        const extendFilterOptions = (source) => {
            if (source.filter.options) {
                return {
                    options: new FilterOptions(source.filter.options),
                };
            }
            return {};
        };

        // prepare filters data
        const filtersDataSource = filters.map(source => ({
            source: {
                ...source,
                ...source.filter ? {
                    filter: {
                        ...source.filter,
                        ...extendFilterOptions(source),
                    },
                } : {},
            },
            ...source.filter ? {
                condition: parseConditionPayload({
                    condition: source.condition,
                    type: source.filter.type,
                }),
            } : {},
        }));

        return filtersDataSource.map(({condition, source}) => ({
            source,
            condition,
            ...source.filter ? {
                filter: new ActiveFilter({
                    filter: source.filter,
                    condition: FilterAdapter.restoreConditionFromPayload({
                        payload: condition,
                        filter: source.filter,
                    }),
                }),
            } : {},
        }));
    }

    /**
     * Returns query array from activeFilters list
     *
     * @param activeFilters
     * @returns {*}
     */
    static restoreQueryFromFilters(activeFilters) {
        return activeFilters.map(activeFilter => ([
            activeFilter.filter.path,
            FilterAdapter.restorePayloadFromCondition({
                condition: activeFilter.condition,
                filter: activeFilter.filter,
            }),
            activeFilter.filter.type,
        ]));
    }

    /**
     * Convert an array of active filters to a string representation.
     * @param {Object|null} filterSet An object with all filters supported by the query component
     * @param {Array} query
     * @returns {string}
     */
    static convertFiltersToString({filterSet, query}) {
        const stringify = (type, filter) => {
            const [rawPath, payload] = filter;
            const path = rawPath.replace(/^\/+/, '');
            if (type === FILTER_TYPES.datetime) {
                const [min, max] = payload;
                if (payload.every(_.isBlank) || max === undefined) {
                    const values = payload.join(filterDelimiters.values);
                    return `${path}${filterDelimiters.definition}${values}`;
                }

                const convert = (value, endOfDay = false) => {
                    if (value) {
                        if (!/^\d{4}-\d{2}-\d{2}/.test(value)) {
                            // support for relative dates
                            return value;
                        }
                        const datetime = moment
                            .utc(value)
                            .tz(timezone.tz(), true);
                        return endOfDay && FilterAdapter.oldFormatExp.test(value)
                            ? datetime.endOf('day').format()
                            : datetime.format();
                    }
                    return '';
                };
                return `${path}${filterDelimiters.definition}${convert(min)}${filterDelimiters.range}${convert(max, true)}`;
            }
            if (type === FILTER_TYPES.numeric) {
                const [min, max] = payload;
                /* eslint-disable arrow-body-style */
                const valueOrEmpty = (val) => {
                    return val === null ? '' : val;
                };
                return `${path}${filterDelimiters.definition}${valueOrEmpty(min)}${filterDelimiters.range}${valueOrEmpty(max)}`;
            }
            if (payload === null
                || (Array.isArray(payload) && payload.length === 0)
                || (typeof payload === 'string' && _.isBlank(payload))) {
                return `${path}${filterDelimiters.definition}`;
            }
            const value = Array.isArray(payload)
                ? payload.join(filterDelimiters.values)
                : payload;
            return `${path}${filterDelimiters.definition}${value}`;
        };
        return query
            .map((filter) => {
                /* eslint-disable no-unused-vars */
                const [pointer, second, type = null] = filter;
                if (filterSet === null || !Reflect.has(filterSet, pointer)) {
                    // no filter set is defined but we get the type directly
                    // from the filter as the third value provided
                    return stringify(type, filter);
                }
                return stringify(filterSet[pointer].type, filter);
            }).join(filterDelimiters.filter);
    }
}
